import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, DataTable, Select, TextInput, Toggle, Drawer, StatusBadge, Tabs, EmptyState } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type CdrDetail } from '@/shared/api/adminApi';
import { apiClient } from '@/shared/api/client';
import { toast } from '@/components/ui/Toast';
import { useAuthStore } from '@/shared/stores/auth';
import { SearchField, FilterRow } from './leads/Toolbar';
import { RecordingDetail } from './recordings/RecordingDetail';
import { RecordingSettings } from './recordings/RecordingSettings';
import { RecordingCard } from './recordings/RecordingCard';
import { Pager } from './recordings/Pager';
import {
  ContactCell, DirectionCell, PlayButton, Row2, directionLabel, fmt, remoteParty, statusLabel, statusTone,
  useCallPlayback, useCampaignQueueNames, whenLabel, type CdrRow as Row,
} from './recordings/cdrShared';

interface AgentOpt { id: number; name: string }

type Quick = 'all' | 'in' | 'out' | 'missed' | 'vm';

const PER_PAGE = 50;

/**
 * יומן השיחות (CDR). recordedOnly = מסך "הקלטות": אותו יומן, מסונן לשיחות
 * עם הקלטה ומוצג ככרטיסי הקלטה. voicemail = תוכן לשונית "תא קולי" בבורר
 * המקטעים של מסך השיחות (נפתחת גם דרך ?tab=voicemail).
 */
export function Cdr({ recordedOnly = false, voicemail }: { recordedOnly?: boolean; voicemail?: ReactNode } = {}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agents, setAgents] = useState<AgentOpt[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Seed the search box from ?search= so an insights drill-down lands pre-filtered.
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [drawer, setDrawer] = useState<Row | null>(null);
  const [detail, setDetail] = useState<CdrDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isSupervisor = useAuthStore((s) => s.user?.role) === 'supervisor';
  // Default OFF (see Recordings.tsx): show the whole business; "my team" is opt-in.
  const [myTeam, setMyTeam] = useState(false);
  const playback = useCallPlayback();
  const sourceOf = useCampaignQueueNames();

  const vmOpen = !!voicemail && searchParams.get('tab') === 'voicemail';

  useEffect(() => {
    void adminApi.agents({ per_page: 500 })
      .then((res) => setAgents(((res.data as AgentOpt[]) ?? []).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => {});
  }, []);

  const params = useCallback(
    () => ({
      direction: direction || undefined,
      status: status || undefined,
      agent_id: agentId ? Number(agentId) : undefined,
      from: from ? `${from} 00:00:00` : undefined,
      to: to ? `${to} 23:59:59` : undefined,
      search: search.trim() || undefined,
      scope: isSupervisor && myTeam ? 'my_team' : undefined,
      has_recording: recordedOnly ? 1 : undefined,
    }),
    [direction, status, agentId, from, to, search, isSupervisor, myTeam, recordedOnly]
  );

  // שינוי מסנן מחזיר לעמוד 1; ה-debounce שבאפקט הבא בולע את הכפילות.
  useEffect(() => { setPage(1); }, [params]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      void adminApi
        .cdr({ ...params(), page, per_page: PER_PAGE })
        .then((res) => {
          setRows(res.data as Row[]);
          const meta = res.meta as { total?: number; last_page?: number } | undefined;
          setTotal(meta?.total ?? (res.data as Row[]).length);
          setLastPage(meta?.last_page ?? 1);
        })
        .catch(() => toast('שגיאה בטעינת היסטוריית שיחות', 'danger'))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [params, page]);

  // בפתיחת ה-drawer — טעינת פרטי השיחה המלאים (הקלטה/תמלול/AI/רגש).
  useEffect(() => {
    if (!drawer) { setDetail(null); return; }
    const callId = drawer.id;
    setDetail(null);
    setDetailLoading(true);
    let active = true;
    void adminApi.cdrDetail(callId)
      .then((d) => { if (active) setDetail(d); })
      .catch(() => { if (active) toast('שגיאה בטעינת פרטי השיחה', 'danger'); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [drawer]);

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await apiClient.get('/admin/cdr/export', { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'merkaziya-cdr.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('שגיאה בייצוא', 'danger');
    } finally {
      setExporting(false);
    }
  }

  function clearFilters() {
    setSearch(''); setDirection(''); setStatus(''); setAgentId(''); setFrom(''); setTo('');
  }

  // בורר המקטעים הוא קיצור לשילובי כיוון/סטטוס; המסננים המלאים נשארים בחלונית "סינון".
  const quick: Quick | '' = vmOpen ? 'vm'
    : status === 'missed' && !direction ? 'missed'
    : status ? ''
    : direction === 'inbound' ? 'in' : direction === 'outbound' ? 'out' : direction ? '' : 'all';

  function setVm(open: boolean) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (open) next.set('tab', 'voicemail'); else next.delete('tab');
      return next;
    }, { replace: true });
  }

  function pickQuick(id: string) {
    if (id === 'vm') { setVm(true); return; }
    setVm(false);
    setDirection(id === 'in' ? 'inbound' : id === 'out' ? 'outbound' : '');
    setStatus(id === 'missed' ? 'missed' : '');
  }

  const quickItems = [
    { id: 'all', label: 'הכל' },
    { id: 'in', label: 'נכנסות' },
    { id: 'out', label: 'יוצאות' },
    { id: 'missed', label: 'לא נענו' },
    ...(voicemail ? [{ id: 'vm', label: 'תא קולי' }] : []),
  ].map((t) => (t.id === quick && t.id !== 'vm' && !loading ? { ...t, count: total } : t));

  // מסננים שלא נראים בבורר המקטעים — מונה על כפתור "סינון".
  const segCovers = !recordedOnly && quick !== '';
  const activeFilters = (segCovers ? 0 : [direction, status].filter(Boolean).length)
    + [agentId, from, to, myTeam].filter(Boolean).length;

  const cols: DataTableColumn<Row>[] = [
    { key: 'started_at', he: 'מתי', render: (r) => <span style={{ color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{whenLabel(r.started_at)}</span> },
    { key: 'direction', he: 'כיוון', render: (r) => <DirectionCell direction={r.direction} /> },
    { key: 'contact', he: 'איש קשר', render: (r) => { const p = remoteParty(r); return <ContactCell number={p.number} lookup={p.lookup} />; } },
    { key: 'agent_name', he: 'נציג', render: (r) => <span style={{ color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{r.agent_name ?? '—'}</span> },
    {
      key: 'source', he: 'קמפיין / תור',
      render: (r) => <span style={{ color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: 170 }} title={sourceOf(r) ?? undefined}>{sourceOf(r) ?? '—'}</span>,
    },
    { key: 'duration_sec', he: 'משך', render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.duration_sec)}</span> },
    { key: 'talk_sec', he: 'דיבור', render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.talk_sec)}</span> },
    {
      // ההשמעה בתוך עמודת הסטטוס (ולא עמודה נפרדת) כדי שהטבלה תיכנס גם ב-1024 בלי גלילה.
      key: 'status', he: 'סטטוס',
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
          <StatusBadge tone={statusTone(r.status)} size="sm">{statusLabel(r.status)}</StatusBadge>
          {r.recording_url && (
            <span style={{ marginInlineStart: 'auto' }}>
              <PlayButton playing={playback.playingId === r.id} busy={playback.busyId === r.id} failed={playback.failedId === r.id} onClick={() => void playback.toggle(r)} />
            </span>
          )}
        </span>
      ),
    },
  ];

  const filtersPanel = filtersOpen && (
    <div className="cdr-reveal">
      <FilterRow>
        <Select value={direction} onChange={setDirection} options={[
          { value: '', label: 'כל הכיוונים' },
          { value: 'inbound', label: 'נכנסות' },
          { value: 'outbound', label: 'יוצאות' },
          { value: 'internal', label: 'פנימיות' },
        ]} />
        <Select value={status} onChange={setStatus} options={[
          { value: '', label: 'כל הסטטוסים' },
          { value: 'completed', label: 'הסתיים' },
          { value: 'answered', label: 'נענה' },
          { value: 'missed', label: 'הוחמץ' },
          { value: 'no-answer', label: 'אין מענה' },
          { value: 'failed', label: 'נכשל' },
        ]} />
        <Select value={agentId} onChange={setAgentId}
          options={[{ value: '', label: 'כל הנציגים' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} dir="ltr" aria-label="מתאריך" style={{ width: 140, height: 36 }} />
          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>–</span>
          <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} dir="ltr" aria-label="עד תאריך" style={{ width: 140, height: 36 }} />
        </div>
        {isSupervisor && (
          <Toggle checked={myTeam} onChange={setMyTeam} label="הצוות שלי" />
        )}
        <button type="button" className="btn ghost" onClick={clearFilters}>נקה</button>
      </FilterRow>
    </div>
  );

  const toolActions = (
    <>
      <button type="button" className="btn soft" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((v) => !v)}>
        <Icon name="filter" size={14} /> סינון{activeFilters > 0 && <span style={{ background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: 99, padding: '0 6px', fontSize: 'var(--t-xs)' }}>{activeFilters}</span>}
      </button>
      <button type="button" className="btn soft" onClick={exportCsv} disabled={exporting || rows.length === 0}>
        <Icon name="download" size={14} /> {exporting ? 'מייצא…' : 'ייצא CSV'}
      </button>
      <button type="button" className="btn square" onClick={() => setSettingsOpen(true)} aria-label="הגדרות הקלטה" title="הגדרות הקלטה">
        <Icon name="settings" size={16} />
      </button>
    </>
  );

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      {/* שם המסך מוצג בסרגל העליון; כאן רק לקוראי מסך. */}
      <h2 className="sr-only">{recordedOnly ? 'הקלטות' : 'שיחות'}</h2>

      {recordedOnly ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <SearchField value={search} onChange={setSearch} placeholder="חיפוש לפי מספר טלפון" />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{toolActions}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Tabs items={quickItems} value={quick} onChange={pickQuick} />
          <span style={{ flex: 1 }} />
          {!vmOpen && <>
            <SearchField value={search} onChange={setSearch} placeholder="חיפוש לפי מספר" />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{toolActions}</div>
          </>}
        </div>
      )}

      {vmOpen ? voicemail : <>
        {filtersPanel}

        {recordedOnly ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loading && rows.length === 0 ? (
              <Card><span style={{ color: 'var(--muted)' }}>טוען…</span></Card>
            ) : rows.length === 0 ? (
              <Card><EmptyState icon="voicemail" title="לא נמצאו הקלטות" /></Card>
            ) : rows.map((r, i) => (
              <RecordingCard
                key={r.id}
                row={r}
                index={i}
                sourceLabel={sourceOf(r)}
                playing={playback.playingId === r.id}
                busy={playback.busyId === r.id}
                failed={playback.failedId === r.id}
                onPlay={() => void playback.toggle(r)}
                onOpen={() => setDrawer(r)}
              />
            ))}
            <Pager page={page} lastPage={lastPage} total={total} pageSize={PER_PAGE} count={rows.length} onPage={setPage} />
          </div>
        ) : (
          <Card padded={false}>
            <DataTable<Row> columns={cols} rows={rows} loading={loading} pageSize={PER_PAGE} onRowClick={setDrawer}
              server={{ page, lastPage, total, onPageChange: setPage }} emptyMessage="לא נמצאו שיחות" />
          </Card>
        )}
      </>}

      <Drawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} title="הגדרות הקלטה">
        {settingsOpen && <RecordingSettings />}
      </Drawer>

      <Drawer isOpen={!!drawer} onClose={() => setDrawer(null)} title={drawer ? `שיחה #${drawer.id}` : ''}>
        {drawer && (
          <div className="stack">
            <Row2 k="כיוון" v={directionLabel(drawer.direction)} />
            <Row2 k="מקור" v={drawer.from_number} mono />
            <Row2 k="יעד" v={drawer.to_number} mono />
            {drawer.agent_name && <Row2 k="נציג" v={drawer.agent_name} />}
            {sourceOf(drawer) && <Row2 k="קמפיין / תור" v={sourceOf(drawer)!} />}
            <Row2 k="התחלה" v={new Date(drawer.started_at).toLocaleString('he-IL')} />
            {drawer.answered_at && <Row2 k="נענתה" v={new Date(drawer.answered_at).toLocaleString('he-IL')} />}
            {drawer.ended_at && <Row2 k="הסתיימה" v={new Date(drawer.ended_at).toLocaleString('he-IL')} />}
            <Row2 k="משך כולל" v={fmt(drawer.duration_sec)} />
            <Row2 k="זמן דיבור" v={fmt(drawer.talk_sec)} />
            <Row2 k="סטטוס" v={statusLabel(drawer.status)} />
            {drawer.disposition && <Row2 k="תוצאה" v={drawer.disposition} />}
            {drawer.hangup_cause && <Row2 k="סיבת ניתוק" v={drawer.hangup_cause} />}

            <RecordingDetail
              detail={detail}
              loading={detailLoading}
              onDetailChange={setDetail}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}
